import { Injectable } from '@angular/core';
import { Http, RequestOptions, Headers, Response } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { LocalStorageService } from './localstorage.service';
import { environment } from '../../environments/environment';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/observable/throw';
import 'rxjs/add/observable/fromPromise';

const API_URL = environment.api_url;

// const API_URL = 'http://localhost:8000/api/v1'

@Injectable()
export class ApiService {

  /******************************************************************/
  /* App specific endpoints
  /******************************************************************/

  login = this._getEndpoint('/auth/azure-login/');
  users = this._getEndpoint('/users');
  admins = this._getEndpoint('/admins');
  me = this._getEndpoint('/users/me');
  spaces = this._getEndpoint('/spaces');
  keysteps = this._getEndpoint('/keysteps');
  keynumbers = this._getEndpoint('/keynumbers');
  agencies = this._getEndpoint('/agencies');
  technicalfiles = this._getEndpoint('/technicalfiles');
  photos = this._getEndpoint('/photos');
  remarkables = this._getEndpoint('/remarkables');
  remarkablephotos = this._getEndpoint('/remarkablephotos');
  contacts = this._getEndpoint('/contacts');
  notifs = this._getEndpoint('/notifs');
  posts = this._getEndpoint('/posts');
  postphotos = this._getEndpoint('/postphotos');
  postfiles = this._getEndpoint('/postfiles');
  subjects = this._getEndpoint('/subjects');


  private _apiRoot: string = API_URL;

  constructor(
    public _http: Http,
    public _storage: LocalStorageService,
  ) { }


  /******************************************************************/
  /* Utils Methods
  /******************************************************************/

  private _baseUrl(path: string): string {
    return this._apiRoot + path;
  }

  private _buildHeaders(multipart = false): Headers {
    const token = this._storage.get('token');
    let headers = {};
    headers['Content-Type'] = 'application/json';
    if (multipart) {
      // headers['Content-Type'] = 'multipart/form-data';
      headers = {};
    }
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    return new Headers(headers);
  }

  public retrieveUrl(path: string): string {
    return this._buildUrl(this._baseUrl(path), {});
  }

  public getToken() {
    return this._storage.get('token');
  }

  private _buildOptions(multipart = false): RequestOptions {
    const headers = this._buildHeaders(multipart);
    const options = new RequestOptions({ headers: headers });
    return options;
  }

  private _handleError(error: Response): Observable<object> {
    return Observable.throw(error.json());
  }

  private _encodeParams(params: object): string {
    return Object.keys(params).map(
      (key) => {
        if (params[key] instanceof Array) {
          return params[key].map((v) => `${key}=${encodeURIComponent(v)}`).join('&');
        } else {
          return `${key}=${encodeURIComponent(params[key])}`;
        }
      }
    ).join('&');
  }

  private _decodeParams(params: string): object {
    return params.split('&').reduce((prev, curr, i, arr) => {
      const obj = curr.split('=');
      prev[obj[0]] = decodeURIComponent(obj[1]);
      return prev;
    }, {});
  }

  private _buildUrl(url: string, params: object): string {
    const getParams: string = this._encodeParams(params);
    if (getParams) {
      return url + '?' + this._encodeParams(params);
    } else {
      return url;
    }
  }

  private checkParams(params: Object, obj: any) {
    let v = true;
    Object.keys(params).forEach((k) => {
      if (obj.hasOwnProperty(k)) {
        if (obj[k] instanceof Object && obj[k].hasOwnProperty('id')) {
          v = v && (obj[k]['id'] === params[k]);
        } else {
          v = v && (obj[k] === params[k]);
        }
      }
    });
    return v;
  }

  private saveData(key: string, data: any) {
    this._storage.setObject(key, data);
  }

  /******************************************************************/
  /* HTTP Methods
  /******************************************************************/

  private _get(url: string, save: boolean = false, params: Object, path: string, id: number | string): Observable<any> {
    if (this._storage.hasKey(path) && !save) {
      let data = this._storage.getObject(path);
      if (data instanceof Array) {
        if (id === 0) {
          data = data.filter((obj) => this.checkParams(params, obj));
        } else {
          data = data.find((obj) => obj.id === id);
        }
      }
      return Observable.of(data);
    } else {
      const options = this._buildOptions();
      return this._http.get(url, options)
        .map((res: Response) => {
          if (save) {
            this.saveData(path, res.json());
          }
          return res.json();
        })
        .catch((error: Response) => this._handleError(error));
    }
  }

  private _post(url: string, data: object = {}, multipart = false): Observable<any> {
    const options = this._buildOptions(multipart);
    return this._http.post(url, data, options)
      .map((res: Response) => res.json())
      .catch((error: Response) => this._handleError(error));
  }

  private _put(url: string, data: object = {}, multipart = false): Observable<any> {
    const options = this._buildOptions(multipart);
    return this._http.put(url, data, options)
      .map((res: Response) => res.json())
      .catch((error: Response) => this._handleError(error));
  }

  private _patch(url: string, data: object = {}, multipart = false): Observable<any> {
    const options = this._buildOptions(multipart);
    return this._http.patch(url, data, options)
      .map((res: Response) => res.json())
      .catch((error: Response) => this._handleError(error));
  }

  private _delete(url: string): Observable<any> {
    const options = this._buildOptions();
    return this._http.delete(url, options)
      .map((res: Response) => res.json())
      .catch((error: Response) => this._handleError(error));
  }

  /******************************************************************/
  /* Endpoint generators
  /******************************************************************/

  private _retrieve(path: string) {
    return (id: number | string, params: Object = {}): Observable<any> => {
      const url = this._buildUrl(this._baseUrl(path) + '/' + id, params);
      return this._get(url, false, params, path, id);
    }
  }

  private _update(path: string) {
    return (id: number | string, data: Object = {}, params: Object = {}, multipart = false): Observable<any> => {
      const url = this._buildUrl(this._baseUrl(path) + '/' + id, params);
      return this._put(url, data, multipart);
    }
  }

  private _partialUpdate(path: string) {
    return (id: number | string, data: Object = {}, params: Object = {}, multipart = false): Observable<any> => {
      const url = this._buildUrl(this._baseUrl(path) + '/' + id, params);
      return this._patch(url, data, multipart);
    }
  }

  private _destroy(path: string) {
    return (id: number | string, params: Object = {}): Observable<any> => {
      const url = this._buildUrl(this._baseUrl(path) + '/' + id, params);
      return this._delete(url);
    }
  }

  private _create(path: string) {
    return (data: Object = {}, params: Object = {}, multipart = false): Observable<any> => {
      const url = this._buildUrl(this._baseUrl(path), params);
      return this._post(url, data, multipart);
    };
  }

  private _list(path: string) {
    return (params: Object = {}, save: boolean = false): Observable<any> => {
      const url = this._buildUrl(this._baseUrl(path), params);
      return this._get(url, save, params, path, 0);
    };
  }

  private _getEndpoint(path: string) {
    return {
      retrieve: this._retrieve(path),
      partialUpdate: this._partialUpdate(path),
      update: this._update(path),
      destroy: this._destroy(path),
      create: this._create(path),
      list: this._list(path),
    }
  }

  /******************************************************************/
  /* Bulk save tools
  /******************************************************************/

  private _syncPromiseAll(promises, cb, i = 0): void {
    if (i < promises.length) {
      promises[i].then((elem) => {
        i++;
        this._syncPromiseAll(promises, cb, i);
      });
    } else {
      cb();
    }
  }

  private syncPromiseAll(promises: Array<Promise<any>>): Promise<any> {
    return new Promise((resolve) => {
      this._syncPromiseAll(promises, (res) => resolve(res));
    });
  }

  public saveAll(endpoints: Array<string>): Promise<any> {
    const promises = endpoints.map((v) => this[v].list({}, true).toPromise())
    return this.syncPromiseAll(promises);
  }

  public getEtag(url): Promise<any> {
    return new Promise((resolve) => {
      this._http
        .head(url)
        .toPromise()
        .then((success) => {
          resolve(success.headers.get('Etag'));
        });
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/services/api.service.ts